import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./PlaylistPage.module.less";
import type { PlaylistResponse, PlaylistState } from "../types/playlist";

const PlaylistPage: React.FC = () => {
  const params = useParams<{ id: string; index?: string }>();
  const id = params.id;
  const indexParam = params.index; // 1-based index as string
  const navigate = useNavigate();
  const fetchedForIdRef = useRef<string | null>(null);
  const [state, setState] = useState<PlaylistState>({
    loading: true,
    error: null,
    data: null,
    currentIndex: null,
  });
  // whether the video description is expanded
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);

  // 获取动漫详情数据
  useEffect(() => {
    const fetchPlaylistData = async () => {
      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));
        // mark as fetching immediately to avoid duplicate fetches (e.g., StrictMode double-invoke)
        fetchedForIdRef.current = id || null;
        const response = await fetch(`https://m.kdm.qzz.io/v2/detail/${id}`, {
          headers: {
            Accept: "*/*",
            "Accept-Language": "zh-CN,zh;q=0.9,en;q=0.8",
            Connection: "keep-alive",
            Origin: "https://m.agedm.io",
            Referer: "https://m.agedm.io/",
            "Sec-Fetch-Dest": "empty",
            "Sec-Fetch-Mode": "cors",
            "Sec-Fetch-Site": "same-site",
            "User-Agent": "Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/137.0.0.0 Safari/537.36",
            "sec-ch-ua": '"Not/A)Brand";v="8", "Chromium";v="137", "Google Chrome";v="137"',
            "sec-ch-ua-mobile": "?1",
            "sec-ch-ua-platform": '"Android"',
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch playlist data");
        }
        const data: PlaylistResponse = await response.json();
        // determine current index (1-based). Do not read indexParam here to avoid
        // triggering re-fetch when only URL index changes. URL index is handled
        // in a separate effect below.
        let resolvedIndex: number | null = null;
        if (data.video.playlists.qiyi && data.video.playlists.qiyi.length > 0) {
          resolvedIndex = 1;
        }

        setState((prev) => ({
          ...prev,
          loading: false,
          data,
          currentIndex: resolvedIndex,
        }));

        // mark this id as fetched successfully to avoid duplicate fetches
        fetchedForIdRef.current = id || null;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : "An error occurred",
        }));
        // clear fetch marker so a retry can occur
        fetchedForIdRef.current = null;
      }
    };

    if (id) {
      // avoid duplicate fetch for same id (helps with StrictMode double-invoke in dev)
      if (fetchedForIdRef.current === id) return;
      fetchPlaylistData();
    }
  }, [id]);

  // keep currentIndex in sync when URL index param changes
  useEffect(() => {
    if (!indexParam) return;
    const parsed = parseInt(indexParam, 10);
    if (Number.isNaN(parsed)) return;
    if (parsed !== state.currentIndex) {
      setState((prev) => ({ ...prev, currentIndex: parsed }));
    }
  }, [indexParam, state.currentIndex]);

  // helper to get the effective current index (1-based)
  const getCurrentIndex = () => {
    const parsed = indexParam ? parseInt(indexParam, 10) : NaN;
    const urlIndex = Number.isNaN(parsed) ? null : parsed;
    const cur = state.currentIndex ?? urlIndex ?? 1;
    if (!Number.isInteger(cur) || cur < 1) return 1;
    return cur;
  };

  // If data is loaded and URL has no index param, push the default index to URL
  useEffect(() => {
    if (!state.data) return;
    if (indexParam) return;
    const defaultIndex = state.currentIndex ?? 1;
    if (!id) return;
    navigate(`/playlist/${id}/${defaultIndex}`, { replace: true });
  }, [state.data, indexParam, navigate, id, state.currentIndex]);

  // ensure active episode in the right-side list is scrolled into view and focused
  useEffect(() => {
    if (!state.data) return;
      // compute current index locally to avoid depending on getCurrentIndex
      const parsed = indexParam ? parseInt(indexParam, 10) : NaN;
      const urlIndex = Number.isNaN(parsed) ? null : parsed;
      const cur = state.currentIndex ?? urlIndex ?? 1;
    const safeCur = Number.isInteger(cur) && cur >= 1 ? cur : 1;
    const el = document.getElementById(`episode-${safeCur}`);
    if (el && typeof el.scrollIntoView === "function") {
      // scroll into view within the sidebar
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      try {
        (el as HTMLElement).focus();
      } catch (e) {
        // ignore
      }
    }
  }, [state.data, state.currentIndex, indexParam]);


  // 播放指定剧集
  const handleEpisodeClick = (index: number) => {
    // navigate will update the URL; index is 1-based
    if (!id) return;
    navigate(`/playlist/${id}/${index}`);
    // optimistic update
    setState((prev) => ({ ...prev, currentIndex: index }));
  };

  // 切换到上一集
  const handlePrevEpisode = () => {
    if (!state.data) return;
    const cur = getCurrentIndex();
    if (cur > 1) {
      handleEpisodeClick(cur - 1);
    }
  };

  // 切换到下一集
  const handleNextEpisode = () => {
    if (!state.data) return;
    const episodes = state.data.video.playlists.qiyi;
    const cur = getCurrentIndex();
    if (cur < episodes.length) {
      handleEpisodeClick(cur + 1);
    }
  };

  if (state.loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (state.error) {
    return <div className={styles.error}>{state.error}</div>;
  }

  if (!state.data) {
    return <div className={styles.error}>No data available</div>;
  }

  const { video, player_jx } = state.data;
  const curIndex = getCurrentIndex();

  return (
    <div className={styles.container}>
      <div className={styles.playerSection}>
        {(() => {
          const episodeIdForIframe = video.playlists.qiyi[curIndex - 1]?.[1];
          if (!episodeIdForIframe) {
            return <div className={styles.noEpisode}>暂无可用剧集</div>;
          }
          const iframeSrc = `${player_jx.vip}${episodeIdForIframe}`;
          return (
            <iframe
              className={styles.iframe}
              src={iframeSrc}
              title="Video Player"
              allowFullScreen
              referrerPolicy="no-referrer"
              onError={(e) => {
                console.error('Iframe failed to load', e);
                // Optionally update state to show an error message
                setState(prev => ({ ...prev, error: '视频加载失败，请稍后重试。' }));
              }}
            />
          );
        })()}
      </div>

      <div className={styles.infoSection}>
        {/* Video info placed at the top of the right section */}
        <div className={styles.videoInfo}>
          <div className={styles.header}>
            <h1>{video.name}</h1>
            <div className={styles.subtitle}>
              {video.name_other} / {video.name_original}
            </div>
          </div>

          <div className={styles.info}>
            <img src={video.cover} alt={video.name} />
            <div className={styles.descriptionContainer}>
              <div
                className={`${styles.description} ${isDescriptionExpanded ? styles.expanded : styles.collapsed}`}
                dangerouslySetInnerHTML={{ __html: video.intro_html }}
              />
              <button 
                className={styles.descriptionToggle}
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              >
                {isDescriptionExpanded ? '收起' : '展开'}
              </button>
            </div>
          </div>
        </div>

        {/* Episode list placed below the video info - always visible */}
        <div className={styles.episodeList}>
          <div className={styles.title}>剧集列表</div>
          <div className={styles.list}>
            {video.playlists.qiyi.map(
              ([name, episodeId]: [string, string], idx: number) => (
                <div
                  id={`episode-${idx + 1}`}
                  key={episodeId}
                  tabIndex={0}
                  className={`${styles.episodeItem} ${
                    idx + 1 === curIndex ? styles.active : ""
                  }`}
                  onClick={() => handleEpisodeClick(idx + 1)}
                >
                  {name}
                </div>
              )
            )}
          </div>
        </div>

        {/* Navigation buttons below episode list */}
        <div className={styles.playerNavigation}>
          <button onClick={handlePrevEpisode} disabled={curIndex <= 1}>
            上一集
          </button>
          <button
            onClick={handleNextEpisode}
            disabled={curIndex >= video.playlists.qiyi.length}
          >
            下一集
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlaylistPage;